import type { CSSProperties } from 'react';
import { forwardRef } from 'react';

import type { Color, Size } from '@snowit/icetypes';
import type { IFlex } from '../Flex/Flex';

import { COLOR, SIZE } from '@snowit/icetypes';

import Flex from '../Flex/Flex';

import clsx from 'clsx';
import css from './Box.module.scss';

type TBoxColor = Color | (string & {});
type TBoxPaddingValue = number | Size;
type TBoxPadding =
  | TBoxPaddingValue
  | [TBoxPaddingValue, TBoxPaddingValue]
  | [TBoxPaddingValue, TBoxPaddingValue, TBoxPaddingValue, TBoxPaddingValue];
type TBoxRadius = Size | (string & {});

export interface BoxProps extends Omit<IFlex, 'ref' | 'tag'> {
  backgroundColor?: TBoxColor;
  borderColor?: TBoxColor;
  /** Border width in pixels. */
  borderWidth?: number;
  /** Padding: all sides, vertical/horizontal, or top/right/bottom/left. */
  padding?: TBoxPadding;
  radius?: TBoxRadius;
}

const resolveColor = (color?: TBoxColor): string | undefined => {
  if (COLOR.includes(color as Color)) return `var(--c-${color})`;
  return color;
};

const resolveRadius = (radius?: TBoxRadius): string | undefined => {
  if (SIZE.includes(radius as Size)) return `var(--s-${radius})`;
  return radius;
};

const resolvePadding = (padding: TBoxPadding): string => {
  const values = Array.isArray(padding) ? padding : [padding];
  return values
    .map((value) => (typeof value === 'number' ? `${value}rem` : `var(--s-${value})`))
    .join(' ');
};

const Box = forwardRef<HTMLDivElement, BoxProps>((props, ref) => {
  const {
    backgroundColor,
    borderColor = 'bg-d3',
    borderWidth = 1,
    padding = 'df',
    radius = 'xxs',
    direction = 'column',
    gap = 1.5,
    className,
    style,
    ...flexProps
  } = props;

  return (
    <Flex
      {...flexProps}
      ref={ref}
      className={clsx(css.box, className)}
      direction={direction}
      gap={gap}
      style={
        {
          ...style,
          '--l-c-bg': resolveColor(backgroundColor),
          '--l-c-border': resolveColor(borderColor),
          '--l-w-border': `${borderWidth}px`,
          '--l-p': resolvePadding(padding),
          '--l-radius': resolveRadius(radius),
        } as CSSProperties
      }
    />
  );
});

export default Box;
